// components/Trainer.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Text,
  useWindowDimensions,
  Platform,
} from 'react-native';
import useTrainerLogic from './TrainerLogic';
import Board from './Board';
import CompletionModal from './CompletionModal';
import { Chess } from 'chess.js';
import SideMenu from './SideMenu';
import TrainerMoveStrip from './TrainerMoveStrip';
import { COLORS, SHADOWS } from '../theme';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const PANEL_MAX_WIDTH = 760;

// Slimmer UI (more board space)
const MOVE_STRIP_HEIGHT = 34;
const MOVE_STRIP_MARGIN_TOP = 6;

const FOOTER_HEIGHT = 32;
const FOOTER_MARGIN_TOP = 6;

const MIN_EDGE_PAD = 2;

const Trainer = ({
  pgn,
  playerColor = 'w',
  onBack,
  openingName,
  appHeaderHeight,
  menuVisible,
  onMenuVisibleChange,
}) => {
  const [isCompletionModalVisible, setCompletionModalVisible] = useState(false);
  const [isTrainingCompleted, setTrainingCompleted] = useState(false);

  const [currentPgn, setCurrentPgn] = useState(pgn);
  const [currentPlayerColor, setCurrentPlayerColor] = useState(playerColor);
  const [shouldHandlePlayOppositeColor, setShouldHandlePlayOppositeColor] = useState(false);

  // Support both controlled and uncontrolled menu visibility
  const [internalMenuVisible, setInternalMenuVisible] = useState(false);
  const isMenuVisible = typeof menuVisible === 'boolean' ? menuVisible : internalMenuVisible;
  const setMenuVisible =
    typeof onMenuVisibleChange === 'function' ? onMenuVisibleChange : setInternalMenuVisible;

  const [selectedElo, setSelectedElo] = useState(() => {
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      const savedElo = localStorage.getItem('selectedElo');
      return savedElo ? parseInt(savedElo, 10) : 1600;
    }
    return 1600;
  });

  const {
    game,
    mistakeSquare,
    hintSquares,
    handleMove,
    showHint,
    resetGame,
    loadPgn,
    bookMovesCount,
    nonBookMovesCount,
    bestMovesCount,
    greatMovesCount,
    mistakesCount,
    hintsCount,
    totalCentipawnLoss,
    finalEngineEvaluation,
  } = useTrainerLogic(currentPgn, currentPlayerColor, selectedElo, (isCompleted) => {
    setTrainingCompleted(!!isCompleted);
    if (isCompleted) setCompletionModalVisible(true);
  });

  const [displayedGame, setDisplayedGame] = useState(() => new Chess(game.fen()));
  const [currentMoveIndex, setCurrentMoveIndex] = useState(() => game.history().length);

  useEffect(() => {
    setCurrentMoveIndex(game.history().length);
  }, [game.history().length]);

  useEffect(() => {
    const newGame = new Chess();
    const history = game.history({ verbose: true });
    for (let i = 0; i < currentMoveIndex; i++) {
      newGame.move(history[i]);
    }
    setDisplayedGame(newGame);
  }, [currentMoveIndex, game]);

  useEffect(() => {
    if (shouldHandlePlayOppositeColor) {
      const moveIndex = displayedGame.history().length;
      const newPgn = displayedGame.pgn();
      loadPgn(newPgn, moveIndex);
      setShouldHandlePlayOppositeColor(false);
    }
  }, [shouldHandlePlayOppositeColor, displayedGame, loadPgn]);

  const { width, height } = useWindowDimensions();

  // Much smaller outer margins; collapse toward ~2px when constrained.
  const edgePadX = useMemo(() => {
    const target = Math.round(width * 0.012); // ~1.2% of width
    return clamp(target, MIN_EDGE_PAD, 10);
  }, [width]);

  const edgePadY = useMemo(() => {
    const target = Math.round(height * 0.010); // ~1.0% of height
    return clamp(target, MIN_EDGE_PAD, 10);
  }, [height]);

  // Slightly tighter panel padding
  const panelPadding = useMemo(() => clamp(Math.round(Math.min(width, height) * 0.012), 8, 12), [
    width,
    height,
  ]);

  // Compute a max possible panel width first.
  const panelWidthCap = useMemo(() => {
    return clamp(width - edgePadX * 2, 260, Math.min(PANEL_MAX_WIDTH, width - MIN_EDGE_PAD * 2));
  }, [width, edgePadX]);

  const innerWidthCap = Math.max(0, panelWidthCap - panelPadding * 2);

  const boardSize = useMemo(() => {
    const reservedH =
      panelPadding * 2 +
      MOVE_STRIP_MARGIN_TOP +
      MOVE_STRIP_HEIGHT +
      FOOTER_MARGIN_TOP +
      FOOTER_HEIGHT +
      4; // safety buffer

    const availableH = Math.max(
      0,
      height - (appHeaderHeight || 0) - edgePadY * 2 - reservedH
    );

    const availableW = Math.max(0, innerWidthCap);

    const size = Math.floor(Math.min(availableW, availableH));
    return clamp(size, 220, 900);
  }, [height, appHeaderHeight, edgePadY, panelPadding, innerWidthCap]);

  // Shrink panel width to the board when height is the limiting factor,
  // so the board does not look “lost” inside a wide panel.
  const panelWidth = useMemo(() => {
    const snug = boardSize + panelPadding * 2;
    return clamp(Math.min(panelWidthCap, snug), 260, panelWidthCap);
  }, [panelWidthCap, boardSize, panelPadding]);

  const handleRestart = () => {
    resetGame(true);
    setCompletionModalVisible(false);
    setCurrentMoveIndex(0);
  };

  const handleSelectOpening = () => {
    setCompletionModalVisible(false);
    onBack();
  };

  const handleCloseModal = () => {
    setCompletionModalVisible(false);
  };

  const onBoardMove = async (move, promotion) => {
    const success = await handleMove(move, promotion);
    if (success !== false) {
      setCurrentMoveIndex(game.history().length);
    }
    return { MoveHasBeenHandled: true, success };
  };

  const metrics = {
    bookMovesCount,
    nonBookMovesCount,
    bestMovesCount,
    greatMovesCount,
    mistakesCount,
    hintsCount,
    totalCentipawnLoss,
    finalEngineEvaluation,
  };

  const goToMove = (moveIndex) => {
    setCurrentMoveIndex(moveIndex + 1);
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleKeyDown = (event) => {
        if (event.key === 'ArrowLeft') {
          setCurrentMoveIndex((prev) => Math.max(prev - 1, 0));
        } else if (event.key === 'ArrowRight') {
          setCurrentMoveIndex((prev) => Math.min(prev + 1, game.history().length));
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
    return undefined;
  }, [game, game.history().length]);

  const handleTrainThisPosition = () => {
    const newPgn = displayedGame.pgn();
    loadPgn(newPgn);
  };

  const handleTrainPositionFromMenu = () => {
    handleTrainThisPosition();
    setMenuVisible(false);
  };

  const handleRestartFromMenu = () => {
    handleRestart();
    setMenuVisible(false);
  };

  const handlePlayOppositeColor = () => {
    const newColor = currentPlayerColor === 'w' ? 'b' : 'w';
    setCurrentPlayerColor(newColor);
    setShouldHandlePlayOppositeColor(true);
    setMenuVisible(false);
  };

  const renderFooter = () => {
    if (isTrainingCompleted) {
      return (
        <View
          style={[
            styles.footerRow,
            { width: boardSize, marginTop: FOOTER_MARGIN_TOP, height: FOOTER_HEIGHT },
          ]}
        >
          <TouchableOpacity
            style={[styles.footerPrimaryBtn, styles.footerBtnLeft]}
            onPress={handleRestart}
            accessibilityLabel="Restart training"
          >
            <View pointerEvents="none" style={styles.footerPrimaryBtnBg} />
            <Text style={styles.footerPrimaryBtnText}>Restart</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerSecondaryBtn}
            onPress={handleSelectOpening}
            accessibilityLabel="Select opening"
          >
            <Text style={styles.footerSecondaryBtnText}>Select Opening</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <TouchableOpacity
        style={[
          styles.hintButton,
          { width: boardSize, marginTop: FOOTER_MARGIN_TOP, height: FOOTER_HEIGHT },
        ]}
        onPress={showHint}
        accessibilityLabel="Hint"
      >
        <View pointerEvents="none" style={styles.hintButtonBg} />
        <Text style={styles.hintButtonText}>Hint</Text>
      </TouchableOpacity>
    );
  };

  return (
    <View
      style={[
        styles.screen,
        {
          paddingHorizontal: edgePadX,
          paddingTop: edgePadY,
          paddingBottom: edgePadY,
        },
      ]}
    >
      <View
        style={[
          styles.panel,
          {
            width: panelWidth,
            padding: panelPadding,
          },
        ]}
      >
        <View style={styles.panelInner}>
          {/* Board */}
          <View style={[styles.boardFrame, { width: boardSize, height: boardSize }]}>
            <Board
              game={displayedGame}
              onMove={onBoardMove}
              playerColor={currentPlayerColor}
              mistakeSquare={mistakeSquare}
              hintSquares={hintSquares}
              isInteractive={currentMoveIndex === game.history().length}
              allowSelectionWhenInactive={true}
              uiVariant="trainer"
            />
          </View>

          {/* Move Strip */}
          <View style={{ width: boardSize, marginTop: MOVE_STRIP_MARGIN_TOP, height: MOVE_STRIP_HEIGHT }}>
            <TrainerMoveStrip
              history={game.history()}
              currentMoveIndex={currentMoveIndex}
              onSelectMove={goToMove}
              height={MOVE_STRIP_HEIGHT}
            />
          </View>

          {/* Footer: Hint OR Completion Actions */}
          {renderFooter()}
        </View>
      </View>

      {/* Trainer Side Menu */}
      <SideMenu
        isVisible={isMenuVisible}
        onClose={() => setMenuVisible(false)}
        onTrainPosition={handleTrainPositionFromMenu}
        selectedElo={selectedElo}
        onEloChange={(elo) => {
          setSelectedElo(elo);
          if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
            localStorage.setItem('selectedElo', String(elo));
          }
        }}
        onRestart={handleRestartFromMenu}
        playerColor={currentPlayerColor}
        onPlayOppositeColor={handlePlayOppositeColor}
      />

      <CompletionModal
        visible={isCompletionModalVisible}
        onRestart={handleRestart}
        onSelectOpening={handleSelectOpening}
        onClose={handleCloseModal}
        metrics={metrics}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    minHeight: 0,
  },

  panel: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: COLORS.trainerPanelBorder,
    backgroundColor: COLORS.trainerPanelBg,
    ...SHADOWS.trainerPanel,
    ...Platform.select({
      web: { backdropFilter: 'blur(10px)' },
      default: {},
    }),
  },

  panelInner: {
    alignItems: 'center',
  },

  boardFrame: {
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(0,0,0,0.18)',
  },

  hintButton: {
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.25)',
  },
  hintButtonBg: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
    backgroundColor: COLORS.trainerHintSolid,
    ...Platform.select({
      web: {
        backgroundImage:
          'linear-gradient(180deg, rgba(245, 158, 11, 0.95), rgba(202, 138, 4, 0.95))',
      },
      default: {},
    }),
  },
  hintButtonText: {
    zIndex: 1,
    position: 'relative',
    color: 'rgba(16, 16, 16, 0.92)',
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 0.2,
  },

  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  footerBtnLeft: {
    marginRight: 8,
  },

  footerPrimaryBtn: {
    flex: 1,
    height: '100%',
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.25)',
    position: 'relative',
  },
  footerPrimaryBtnBg: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
    backgroundColor: COLORS.trainerHintSolid,
    ...Platform.select({
      web: {
        backgroundImage:
          'linear-gradient(180deg, rgba(245, 158, 11, 0.95), rgba(202, 138, 4, 0.95))',
      },
      default: {},
    }),
  },
  footerPrimaryBtnText: {
    zIndex: 1,
    position: 'relative',
    color: 'rgba(16, 16, 16, 0.92)',
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 0.2,
  },

  footerSecondaryBtn: {
    flex: 1,
    height: '100%',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: COLORS.trainerPanelBorder,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  footerSecondaryBtnText: {
    color: COLORS.trainerPanelBorder,
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 0.2,
  },
});

export default Trainer;
